import { useCarrierDetailQuery, useProductsByCarrierQuery, useRetryContractExtractionMutation } from "@qw/graphql";
import { parseError } from "@qw/qw-common";
import {
  Badge,
  Button,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Column,
  ColumnContent,
  Icon,
  Spinner,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  uploadCarrierContract,
  useToast,
} from "@qw/ui";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { CarrierForm } from "./carrier-form";
import { CommissionRatesTable } from "./commission-rates-table";
import { ContractAgreementDetails } from "./contract-agreement-details";
import { UploadContractDialog } from "./upload-contract-dialog";

export function CarrierDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isUploading, setIsUploading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);

  const {
    data: { carrier } = {},
    loading,
    refetch,
  } = useCarrierDetailQuery({
    variables: { id: id! },
    skip: !id,
    onError: (error) => {
      toast({ title: "Error", description: parseError(error), variant: "destructive" });
    },
  });

  const { data: productsData } = useProductsByCarrierQuery({
    variables: { carrierSlug: carrier?.slug ?? "" },
    skip: !carrier?.slug,
  });

  const [retryExtraction] = useRetryContractExtractionMutation({
    onCompleted: () => {
      toast({ title: "Re-extraction started", description: "Agreement data extraction is in progress." });
      void refetch();
    },
    onError: (error) => {
      toast({ title: "Re-extraction failed", description: parseError(error), variant: "destructive" });
    },
  });

  const productNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const product of productsData?.productsByCarrier ?? []) {
      map.set(product.id, product.name);
    }
    return map;
  }, [productsData?.productsByCarrier]);

  const handleUploadConfirm = async (file: File, appetiteProductIds: string[]) => {
    if (!id) {
      return;
    }

    try {
      setIsUploading(true);

      const result = await uploadCarrierContract(file, id, appetiteProductIds);

      if (!result.success) {
        toast({ title: "Upload failed", variant: "destructive" });
        return;
      }

      toast({ title: "Contract uploaded", description: "Agreement data extraction is in progress." });
      setDialogOpen(false);
      void refetch();
    } catch (error) {
      toast({ title: "Upload failed", description: String((error as Error).message ?? error), variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex-auto flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (!carrier) {
    return (
      <Column className="flex-auto bg-background">
        <ColumnContent className="p-4">
          <div className="text-center text-muted-foreground py-12">Carrier not found.</div>
        </ColumnContent>
      </Column>
    );
  }

  return (
    <Column className="flex-auto bg-background">
      <ColumnContent className="min-h-0">
        <Tabs defaultValue="details" className="flex h-full min-h-0 flex-col gap-0">
          <div className="flex shrink-0 items-center justify-between border-b p-4">
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                display="icon"
                size="sm"
                onClick={() => navigate("/products")}
                aria-label="Back to products"
              >
                <Icon icon="arrow_back" />
              </Button>
              <h1 className="font-semibold">{carrier.name}</h1>
            </div>

            <TabsList>
              <TabsTrigger value="details">Details</TabsTrigger>
              <TabsTrigger value="contracts">Contracts</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="details" className="flex min-h-0 flex-1 flex-col">
            <Column className="min-h-0 p-4">
              <ColumnContent>
                <h2 className="text-lg font-semibold mb-4">Carrier Details</h2>
                <CarrierForm carrier={carrier} />
              </ColumnContent>
            </Column>
          </TabsContent>

          <TabsContent value="contracts" className="flex flex-1 flex-col p-4 overflow-auto">
            <div className="shrink-0 flex justify-end py-3">
              <Button variant="outline" size="sm" disabled={isUploading} onClick={() => setDialogOpen(true)}>
                <Icon icon="upload" />
                Upload Contract PDF
              </Button>
            </div>

            <div className="flex-1 space-y-3 min-w-0">
              {carrier.contracts && carrier.contracts.length > 0 ? (
                carrier.contracts.map((contract) => (
                  <div key={contract.id} className="rounded-lg border bg-card p-4 space-y-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="flex flex-1 gap-2 items-baseline">
                        {contract.fileUpload ? (
                          <a
                            href={`/api/files/${contract.fileUpload.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm font-medium hover:underline truncate block"
                          >
                            {contract.fileUpload.filename}
                          </a>
                        ) : (
                          <span className="text-sm font-medium text-muted-foreground">Seeded contract</span>
                        )}
                        {contract.appetiteProductIds && contract.appetiteProductIds.length > 0 && (
                          <p className="text-xs text-muted-foreground">
                            {contract.appetiteProductIds.map((id) => productNameMap.get(id) ?? id).join(", ")}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <p className="text-xs text-muted-foreground mt-0.5">
                          Uploaded {new Date(contract.createdAt).toLocaleDateString()}
                        </p>
                        {contract.extractionStatus !== "COMPLETED" && (
                          <ExtractionStatusBadge status={contract.extractionStatus} />
                        )}
                        {contract.fileUploadId &&
                          (contract.extractionStatus === "COMPLETED" || contract.extractionStatus === "FAILED") && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => void retryExtraction({ variables: { contractId: contract.id } })}
                            >
                              <Icon icon="refresh" className="mr-1" />
                              Re-extract
                            </Button>
                          )}
                      </div>
                    </div>
                    <Collapsible defaultOpen={false} className="border-t border-border pt-3">
                      <CollapsibleTrigger className="flex w-full items-center gap-2 py-1.5 text-left text-base font-medium text-muted-foreground hover:text-foreground transition-colors [&[data-state=open]>[data-chevron]]:rotate-90">
                        <Icon icon="chevron_right" className="h-4 w-4 shrink-0 transition-transform" data-chevron />
                        Agreement details
                      </CollapsibleTrigger>
                      <CollapsibleContent className="pt-1">
                        <ContractAgreementDetails contract={contract} />
                      </CollapsibleContent>
                    </Collapsible>
                    {(contract.commissions?.length ?? 0) > 0 && (
                      <Collapsible defaultOpen={false} className="border-t border-border pt-3">
                        <CollapsibleTrigger className="flex w-full items-center gap-2 py-1.5 text-left text-base font-medium text-muted-foreground hover:text-foreground transition-colors [&[data-state=open]>[data-chevron]]:rotate-90">
                          <Icon icon="chevron_right" className="h-4 w-4 shrink-0 transition-transform" data-chevron />
                          Commission rates
                        </CollapsibleTrigger>
                        <CollapsibleContent className="pt-1">
                          <CommissionRatesTable
                            commissions={contract.commissions ?? []}
                            productNameMap={productNameMap}
                          />
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                    {(contract.incentives?.length ?? 0) > 0 && (
                      <Collapsible defaultOpen={false} className="border-t border-border pt-3">
                        <CollapsibleTrigger className="flex w-full items-center gap-2 py-1.5 text-left text-base font-medium text-muted-foreground hover:text-foreground transition-colors [&[data-state=open]>[data-chevron]]:rotate-90">
                          <Icon icon="chevron_right" className="h-4 w-4 shrink-0 transition-transform" data-chevron />
                          Incentives
                        </CollapsibleTrigger>
                        <CollapsibleContent className="pt-1">
                          <div className="space-y-2">
                            {(contract.incentives ?? []).map((incentive) => (
                              <IncentiveCard key={incentive.id} incentive={incentive} />
                            ))}
                          </div>
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </div>
                ))
              ) : (
                <div className="rounded-lg border bg-card p-4">
                  <p className="text-sm text-muted-foreground">No contracts uploaded yet.</p>
                </div>
              )}
            </div>
            {carrier.slug && (
              <UploadContractDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onConfirm={handleUploadConfirm}
                carrierSlug={carrier.slug}
                isUploading={isUploading}
              />
            )}
          </TabsContent>
        </Tabs>
      </ColumnContent>
    </Column>
  );
}

function ExtractionStatusBadge({ status }: { status: string }) {
  const variants: Record<string, "default" | "secondary" | "destructive" | "outline"> = {
    PENDING: "secondary",
    PROCESSING: "default",
    COMPLETED: "outline",
    FAILED: "destructive",
  };
  return <Badge variant={variants[status] ?? "secondary"}>{status}</Badge>;
}

interface IncentiveProps {
  incentive: {
    id: string;
    incentiveType: string;
    summary: string;
    details?: unknown;
  };
}

function IncentiveCard({ incentive }: IncentiveProps) {
  const typeLabels: Record<string, string> = {
    contingent_commission: "Contingent Commission",
    profit_share: "Profit Share",
    volume_bonus: "Volume Bonus",
    growth_bonus: "Growth Bonus",
    other: "Other",
  };

  return (
    <div className="flex gap-4">
      <div>
        <Badge variant="secondary" className="font-normal">
          {typeLabels[incentive.incentiveType] ?? incentive.incentiveType}
        </Badge>
        <p className="text-sm mt-2 text-foreground">{incentive.summary}</p>
      </div>
      <div>
        {typeof incentive.details === "object" && incentive.details !== null ? (
          <IncentiveDetails details={incentive.details as Record<string, unknown>} />
        ) : null}
      </div>
    </div>
  );
}

function IncentiveDetails({ details }: { details: Record<string, unknown> }) {
  const entries = Object.entries(details).filter(
    ([, value]) => value !== null && value !== undefined && !(Array.isArray(value) && value.length === 0)
  );

  if (entries.length === 0) {
    return null;
  }

  return (
    <div className="mt-1 text-xs text-muted-foreground space-y-0.5">
      {entries.map(([key, value]) => (
        <div key={key}>
          <span className="font-medium capitalize">{key.replace(/([A-Z])/g, " $1").trim()}: </span>
          <span>{Array.isArray(value) ? (value as string[]).join(", ") : String(value)}</span>
        </div>
      ))}
    </div>
  );
}
